import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import useEmisionesCo2Adheridos from "../../../hooks/administrador/emisiones/useEmisionesCo2Adheridos";
import "../../../styles/administrador.css";

const EmisionesCo2Adheridos = ({ year, mes, token, idAdministrador }) => {
  const chartRef = useRef(null);
  const { data, loading, error } = useEmisionesCo2Adheridos(
    year,
    mes,
    token,
    idAdministrador
  );

  useEffect(() => {
    if (loading || !chartRef.current) return;

    if (error || !Array.isArray(data) || data.length === 0) {
      if (chartRef.current) {
        chartRef.current.innerHTML =
          "<p style='text-align: center; color: black;'>No hay datos disponibles</p>";
      }
      return;
    }

    const chartDom = chartRef.current;
    let myChart = echarts.getInstanceByDom(chartDom);
    if (myChart) myChart.dispose();
    myChart = echarts.init(chartDom);

    const colorPalette = [
      "#d1dc5a",
      "#6ac5cb",
      "#45444e",
      "#a3b85b",
      "#3a6b74",
      "#7d8a2e",
      "#3d9199",
      "#62615e",
      "#879b4a",
      "#2b5258",
    ];

    const dataProcesada = {
      empresas: new Set(),
      residuos: new Set(),
      seriesData: {},
      totalCO2PorEmpresa: {},
    };

    const totalEmisiones = data.reduce(
      (sum, item) => sum + parseFloat(item.Total_CO2_Ahorrado_Tn || 0),
      0
    );

    data.forEach((item) => {
      const empresa = item.Razon_Social;
      const residuo = item.codigo_residuo;
      const totalCO2 = parseFloat(item.Total_CO2_Ahorrado_Tn || 0);

      dataProcesada.empresas.add(empresa);
      dataProcesada.residuos.add(residuo);

      if (!dataProcesada.seriesData[empresa]) {
        dataProcesada.seriesData[empresa] = {};
        dataProcesada.totalCO2PorEmpresa[empresa] = 0;
      }

      dataProcesada.seriesData[empresa][residuo] = totalCO2;
      dataProcesada.totalCO2PorEmpresa[empresa] += totalCO2;
    });

    let topEmpresas = [...dataProcesada.empresas]
      .sort(
        (a, b) =>
          dataProcesada.totalCO2PorEmpresa[b] -
          dataProcesada.totalCO2PorEmpresa[a]
      )
      .slice(0, 10);

    const otrosTotalCO2 = [...dataProcesada.empresas]
      .filter((empresa) => !topEmpresas.includes(empresa))
      .reduce(
        (sum, empresa) => sum + dataProcesada.totalCO2PorEmpresa[empresa],
        0
      );

     
    const otrosPorResiduo = {};
    [...dataProcesada.empresas]
      .filter((empresa) => !topEmpresas.includes(empresa))
      .forEach((empresa) => {
        Object.keys(dataProcesada.seriesData[empresa]).forEach((residuo) => {
          if (!otrosPorResiduo[residuo]) {
            otrosPorResiduo[residuo] = 0;
          }
          otrosPorResiduo[residuo] +=
            dataProcesada.seriesData[empresa][residuo];
        });
      });
    if (otrosTotalCO2 > 0) {
      topEmpresas = topEmpresas.filter((empresa) => empresa !== "OTROS");  
      topEmpresas.unshift("OTROS");  
      dataProcesada.totalCO2PorEmpresa["OTROS"] = otrosTotalCO2;
    }

     
    const yAxisData = topEmpresas.map((empresa) => {
      if (empresa.length > 13) {
        return empresa.substr(0, 13) + "...";  
      }
      return empresa;
    });

     
    const series = [...dataProcesada.residuos].map((residuo, index) => ({
      name: residuo,
      type: "bar",
      stack: "total",
      data: yAxisData.map((empresaTruncada, i) => {
        const empresaCompleta = topEmpresas[i];  
        if (empresaCompleta === "OTROS") {
          return otrosPorResiduo[residuo] || 0;  
        }
        return dataProcesada.seriesData[empresaCompleta]?.[residuo] || 0;
      }),
      itemStyle: {
        color: colorPalette[index % colorPalette.length],
      },
    }));

     

     
    const option = {
      title: {
        text: "Emisiones de CO2 ahorradas \npor miembro adherido",
        left: "center",
        subtext: `TOTAL AHORRADO: ${totalEmisiones.toLocaleString()} Tn`,
        subtextStyle: {
          fontSize: "12px",
          fontWeight: "bold",
        },
        textStyle: {
          fontSize: 18,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params) {
          let empresaCompleta = topEmpresas[params[0].dataIndex];  
          let totalCO2 = dataProcesada.totalCO2PorEmpresa[empresaCompleta] || 0;

          let tooltipHTML = `<div style="font-weight:bold;margin-bottom:5px;">${empresaCompleta}</div>`;

          params.forEach((param) => {
            if (param.value > 0) {
              tooltipHTML += `
          <div>
            <span style="display:inline-block;width:10px;height:10px;background:${
              param.color
            };margin-right:5px;"></span>
            <span style="font-weight:bold;">${param.seriesName}:</span> 
            <span>${param.value.toLocaleString("es-ES")} Tn</span>
          </div>`;
            }
          });

          tooltipHTML += `
      <div style="margin-top:5px;padding-top:5px;border-top:1px solid #eee;">
        <span style="font-weight:bold;">Total CO₂ ahorrado:</span> ${totalCO2.toFixed(
          2
        )} Tn
      </div>`;

          return tooltipHTML;
        },
      },
      legend: {
        data: [...dataProcesada.residuos],
        bottom: "5%",
        left: "center",
        type: "scroll",
      },
      grid: {
        left: "15%",  
        right: "4%",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: "Toneladas (Tn)",
        axisLabel: {
          formatter: function (value) {
            return value >= 1000 ? (value / 1000).toFixed(1) + "K" : value;
          },
        },
      },
      yAxis: {
        type: "category",
        data: yAxisData,  
        axisLabel: {
          fontSize: 9,
          fontWeight: "bold",
        },
      },
      series: series,
    };

    myChart.setOption(option);

    const updateChart = () => {
      myChart.resize();
    };

    window.addEventListener("resize", updateChart);

    return () => {
      window.removeEventListener("resize", updateChart);
      if (myChart && !myChart.isDisposed()) {
        myChart.dispose();
      }
    };
  }, [data, loading, error]);

  if (loading) {
    return (
      <div className="grafica loading">
        Cargando datos de emisiones CO2 por adheridos...
      </div>
    );
  }

  return <div className="grafica" ref={chartRef}></div>;
};

export default EmisionesCo2Adheridos;
